import type { Connection, CreateConnectionRequest, EnvClass } from '@stride/shared'
import type { IConnectionService, RotateSecret } from './IConnectionService.interface'

// ── Seeded demo connections ───────────────────────────────────────────────────

const DEMO_CONNECTIONS: Connection[] = [
  {
    id:           'conn-demo-sf-001',
    kind:         'salesforce',
    method:       'authurl',
    label:        'ARM Source (Production)',
    instanceUrl:  'https://stratuscarta-c.my.salesforce.com',
    username:     'deploy@stratuscarta.com',
    health:       'healthy',
    lastCheckedAt: new Date(Date.now() - 60_000).toISOString(),
    createdAt:    new Date(Date.now() - 7 * 86_400_000).toISOString(),
  },
  {
    id:           'conn-demo-sf-002',
    kind:         'salesforce',
    method:       'oauth',
    label:        'ARM Target (Developer Sandbox)',
    instanceUrl:  'https://st1750093282865-dev-ed.develop.my.salesforce.com',
    username:     'dev@stratuscarta.com',
    health:       'healthy',
    lastCheckedAt: new Date(Date.now() - 120_000).toISOString(),
    createdAt:    new Date(Date.now() - 3 * 86_400_000).toISOString(),
  },
]

// ── MockConnectionService ─────────────────────────────────────────────────────

export class MockConnectionService implements IConnectionService {
  private readonly connections = new Map<string, Connection>(
    DEMO_CONNECTIONS.map((c) => [c.id, { ...c }]),
  )

  listConnections(): Promise<Connection[]> {
    return Promise.resolve([...this.connections.values()])
  }

  createConnection(req: CreateConnectionRequest): Promise<Connection> {
    const id  = globalThis.crypto.randomUUID()
    const now = new Date().toISOString()

    if (req.kind === 'salesforce' && req.method === 'authurl') {
      const conn: Connection = {
        id,
        kind:        'salesforce',
        method:      'authurl',
        label:       req.label,
        instanceUrl: `https://demo-${id.slice(0, 8)}.my.salesforce.com`,
        health:      'unchecked',
        createdAt:   now,
      }
      this.connections.set(id, conn)
      return Promise.resolve({ ...conn })
    }

    if (req.kind === 'jira' && req.method === 'apitoken') {
      const conn: Connection = {
        id,
        kind:    'jira',
        method:  'apitoken',
        label:   req.label,
        baseUrl: req.baseUrl,
        health:  'unchecked',
        createdAt: now,
        ...(req.projectKey !== undefined && { projectKey: req.projectKey }),
      }
      this.connections.set(id, conn)
      return Promise.resolve({ ...conn })
    }

    if (req.kind === 'git' && req.method === 'pat') {
      const conn: Connection = {
        id,
        kind:    'git',
        method:  'pat',
        label:   req.label,
        health:  'unchecked',
        createdAt: now,
        ...(req.repoUrl !== undefined && { repoUrl: req.repoUrl }),
      }
      this.connections.set(id, conn)
      return Promise.resolve({ ...conn })
    }

    return Promise.reject(new Error('Unsupported connection kind/method'))
  }

  deleteConnection(id: string): Promise<void> {
    if (!this.connections.has(id)) {
      return Promise.reject(new Error(`Connection '${id}' not found`))
    }
    this.connections.delete(id)
    return Promise.resolve()
  }

  updateConnection(id: string, patch: { deployBranch?: string; label?: string; isArmSource?: boolean; envClass?: EnvClass }): Promise<Connection> {
    const conn = this.connections.get(id)
    if (!conn) return Promise.reject(new Error(`Connection '${id}' not found`))
    // Mirror the single-source rule: setting one source clears the others.
    if (patch.isArmSource === true) {
      for (const [cid, c] of this.connections) {
        if (cid !== id && c.isArmSource) this.connections.set(cid, { ...c, isArmSource: false })
      }
    }
    const updated: Connection = {
      ...conn,
      ...(patch.deployBranch !== undefined && { deployBranch: patch.deployBranch }),
      ...(patch.label !== undefined && { label: patch.label }),
      ...(patch.isArmSource !== undefined && { isArmSource: patch.isArmSource }),
    }
    this.connections.set(id, updated)
    return Promise.resolve({ ...updated })
  }

  rotateCredential(id: string, _secret: RotateSecret): Promise<Connection> {
    const conn = this.connections.get(id)
    if (!conn) return Promise.reject(new Error(`Connection '${id}' not found`))
    // Mirrors the API: the id is preserved, and health drops to unchecked because the new secret has not
    // been used yet. A mock that reported healthy would let a UI bug through unseen.
    const updated: Connection = { ...conn, health: 'unchecked' }
    this.connections.set(id, updated)
    return Promise.resolve(updated)
  }

  checkHealth(id: string): Promise<Connection> {
    const conn = this.connections.get(id)
    if (!conn) return Promise.reject(new Error(`Connection '${id}' not found`))
    const updated: Connection = {
      ...conn,
      health:        'healthy',
      lastCheckedAt: new Date().toISOString(),
    }
    this.connections.set(id, updated)
    return Promise.resolve({ ...updated })
  }

  getSalesforceOAuthUrl(label: string): string {
    const params = new URLSearchParams({ label })
    return `/api/connections/oauth/sf/start?${params.toString()}`
  }

  getGitOAuthUrl(label: string, repoUrl?: string): string {
    const params = new URLSearchParams({ label })
    if (repoUrl !== undefined) params.set('repoUrl', repoUrl)
    return `/api/connections/oauth/git/start?${params.toString()}`
  }
}
